import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, ApiError } from "@/api/client";

interface RecentReceipt {
  id: string;
  uploaded_at_sec: number;
  verified: boolean;
  decision_count: number;
  session_hash: string;
}

interface DashboardData {
  now: number;
  window_sec: number;
  receipts: {
    total: number;
    unverified: number;
    last_24h: number;
    unverified_24h: number;
  };
  packs: {
    total: number;
    published_to: number;
    canary: number;
    stable: number;
    pending: number;
  };
  fleet: {
    size: number;
    cohorts: Record<string, number>;
  };
  recent_receipts: RecentReceipt[];
}

function fmtTime(unixSec: number) {
  if (!unixSec) return "—";
  return new Date(unixSec * 1000).toISOString().replace("T", " ").slice(0, 19) + "Z";
}

function StatCard({
  label, value, sub, tone = "neutral",
}: { label: string; value: string | number; sub?: string; tone?: "neutral" | "good" | "warn" }) {
  const ring =
    tone === "good"
      ? "border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/30"
      : tone === "warn"
      ? "border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30"
      : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800";
  return (
    <div className={`rounded border ${ring} px-4 py-3`}>
      <div className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold tabular-nums mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export function DashboardPage() {
  const q = useQuery<DashboardData>({
    queryKey: ["dashboard"],
    queryFn: () => api<DashboardData>("/api/v1/dashboard"),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  if (q.isLoading) return <p className="text-slate-500 dark:text-slate-400">Loading…</p>;
  if (q.error) {
    if (q.error instanceof ApiError && q.error.status === 401) {
      return (
        <div className="rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2 text-sm">
          Sign in as an operator to view the dashboard.
        </div>
      );
    }
    return (
      <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300">
        Could not load dashboard: {(q.error as Error).message}
      </div>
    );
  }
  const d = q.data!;
  const verifiedPct =
    d.receipts.total > 0
      ? Math.round(((d.receipts.total - d.receipts.unverified) / d.receipts.total) * 1000) / 10
      : null;

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Live snapshot. Auto-refreshes every 30 s.
        </p>
      </header>

      <section className="mb-6">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Receipts</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            label="Total receipts"
            value={d.receipts.total.toLocaleString()}
            sub={verifiedPct !== null ? `${verifiedPct}% verified` : undefined}
          />
          <StatCard
            label="Last 24 h"
            value={d.receipts.last_24h.toLocaleString()}
            sub={`${(d.receipts.last_24h / 86400 * 60).toFixed(2)}/min avg`}
          />
          <StatCard
            label="Unverified (all-time)"
            value={d.receipts.unverified.toLocaleString()}
            tone={d.receipts.unverified > 0 ? "warn" : "good"}
            sub={d.receipts.unverified === 0 ? "all clean" : "tamper attempts logged"}
          />
          <StatCard
            label="Unverified (24 h)"
            value={d.receipts.unverified_24h.toLocaleString()}
            tone={d.receipts.unverified_24h > 0 ? "warn" : "good"}
            sub={d.receipts.unverified_24h === 0 ? "no recent failures" : undefined}
          />
        </div>
      </section>

      <section className="mb-6">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Rule packs</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            label="Pack versions"
            value={d.packs.total.toLocaleString()}
            sub={`published through v${d.packs.published_to}`}
          />
          <StatCard label="Stable" value={d.packs.stable.toLocaleString()} tone="good" />
          <StatCard label="Canary" value={d.packs.canary.toLocaleString()} tone="warn" />
          <StatCard label="Pending" value={d.packs.pending.toLocaleString()} />
        </div>
      </section>

      <section className="mb-6">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Fleet</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            label="Enrolled devices"
            value={d.fleet.size.toLocaleString()}
          />
          {Object.entries(d.fleet.cohorts).map(([cohort, count]) => (
            <StatCard
              key={cohort}
              label={`Cohort: ${cohort}`}
              value={count.toLocaleString()}
              tone={cohort === "canary" ? "warn" : "neutral"}
            />
          ))}
          {d.fleet.size === 0 && (
            <div className="col-span-3 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700 px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
              No devices enrolled yet — see <Link to="/fleet" className="text-blue-700 hover:underline">/fleet</Link> for enrollment.
            </div>
          )}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Recent receipts</h2>
          <Link to="/receipts" className="text-xs text-blue-700 hover:underline">
            view all →
          </Link>
        </div>
        <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-700 text-left text-xs uppercase tracking-wide text-slate-600 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2">Time (UTC)</th>
                <th className="px-3 py-2">ID</th>
                <th className="px-3 py-2">Decisions</th>
                <th className="px-3 py-2">Session hash</th>
                <th className="px-3 py-2">Verified</th>
              </tr>
            </thead>
            <tbody>
              {d.recent_receipts.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500 dark:text-slate-400">No receipts yet.</td></tr>
              )}
              {d.recent_receipts.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700">
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{fmtTime(r.uploaded_at_sec)}</td>
                  <td className="px-3 py-2 font-mono">
                    <Link to={`/receipts/${r.id}`} className="text-blue-700 hover:underline">{r.id}</Link>
                  </td>
                  <td className="px-3 py-2 tabular-nums">{r.decision_count}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.session_hash.slice(0, 16)}…</td>
                  <td className="px-3 py-2">
                    {r.verified ? (
                      <span className="rounded bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 text-xs font-semibold">YES</span>
                    ) : (
                      <span className="rounded bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 px-2 py-0.5 text-xs font-semibold">NO</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
